import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetPlannerEventsQuery } from '@/store/api/eventService/eventServiceApi';
import { useGetPlannerBuildingsQuery } from '@/store/api/spaceConfigService/spaceConfigServiceApi';
import type { EventSummary } from '@/store/models/events/entities';
import {
  mapEventSummariesToPlannerRows,
  mapStatusesToEventStates,
} from '@/utils/plannerEventMapper';
import { buildSeriesOrdinalTitleMap } from '@/utils/seriesEventTitle';
import { toCalendarDate } from '@/utils/eventFiltersToParams';
import { telemetry } from '@/services/telemetry';
import { TELEMETRY_COMPONENT, EVENT_TYPES } from '@/constants';
import type { GetEventsParams } from '@/store/models/events/params';
import type { PlannerEventRow, EventFilters } from '@/types/planner.types';

const DEFAULT_PAGE_SIZE = 25;

export type PlannerSortBy = 'eventStartDate' | 'createdAt' | 'lastUpdatedAt';
export type PlannerSortOrder = 'asc' | 'desc';

interface PlannerApiFilters {
  status?: string;
  owner?: string;
  buildingId?: GetEventsParams['buildingId'];
  startDate?: string;
  endDate?: string;
  submittedStartDate?: string;
  submittedEndDate?: string;
}

export interface UsePlannerEventsOptions {
  initialParams?: GetEventsParams;
  skip?: boolean;
}

export interface UsePlannerEventsResult {
  events: readonly PlannerEventRow[];
  isLoadingInitial: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  batchError: boolean;
  endReached: boolean;
  totalCount: number | undefined;
  sortBy?: PlannerSortBy;
  sortOrder?: PlannerSortOrder;
  pageSize: number;
  loadMore: () => void;
  retryLastBatch: () => void;
  refetch: () => void;
  setSortBy: (sortBy: PlannerSortBy, order: PlannerSortOrder) => void;
  setFilters: (filters: EventFilters) => void;
  setSearch: (search: string) => void;
  clearFilters: () => void;
  applyRowMutation: (eventId: string, patch: Partial<PlannerEventRow>) => void;
}

export function usePlannerEvents(options?: UsePlannerEventsOptions): UsePlannerEventsResult {
  const pageSize = options?.initialParams?.pageSize ?? DEFAULT_PAGE_SIZE;

  const [sortBy, setSortByState] = useState<PlannerSortBy | undefined>(
    options?.initialParams?.sortBy
  );
  const [sortOrder, setSortOrderState] = useState<PlannerSortOrder | undefined>(
    options?.initialParams?.sortOrder
  );
  const [apiFilters, setApiFilters] = useState<PlannerApiFilters>({});
  // Free-text search term sent to the backend for an exhaustive, server-side search
  // across the entire event set (not just the page already loaded into the grid).
  const [search, setSearchState] = useState<string | undefined>(options?.initialParams?.search);

  // Cursor for the next request. `undefined` means "fetch batch 0".
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  // Tracks the batch index for telemetry and coalescing.
  const [batchIndex, setBatchIndex] = useState(0);
  // Accumulating window across batches.
  const [accumulated, setAccumulated] = useState<readonly PlannerEventRow[]>([]);
  const [_accumulatedSource, setAccumulatedSource] = useState<readonly EventSummary[]>([]);
  const [endReached, setEndReached] = useState(false);
  const [batchError, setBatchError] = useState(false);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);

  // Refs for coalescing, telemetry timing, and idempotent append. Do not trigger re-renders.
  const inFlightBatchIndexRef = useRef<number | null>(null);
  const batchStartedAtRef = useRef<number>(performance.now());
  const lastProcessedBatchIndexRef = useRef<number>(-1);
  // Tracks the data identity last processed so we can detect tag-invalidation refetches.
  const lastProcessedDataRef = useRef<typeof data>(undefined);
  // Bridges the gap between reset() clearing accumulated and isFetching flipping true.
  const resettingRef = useRef(false);
  const sawFetchAfterResetRef = useRef(false);
  const isFetchingRef = useRef(false);
  const dataRef = useRef<typeof data>(undefined);
  const dataAtResetRef = useRef<typeof data>(undefined);

  const currentParams = useMemo<GetEventsParams>(() => {
    return {
      ...options?.initialParams,
      ...apiFilters,
      search,
      pageSize,
      continuationToken: cursor,
      sortBy,
      sortOrder,
    };
  }, [pageSize, cursor, sortBy, sortOrder, apiFilters, search, options?.initialParams]);

  const { data, isLoading, isFetching, isError, refetch } = useGetPlannerEventsQuery(
    options?.skip ? undefined : currentParams,
    { skip: options?.skip }
  );
  const { data: plannerBuildingsData, isLoading: isBuildingsLoading } = useGetPlannerBuildingsQuery(
    undefined,
    { skip: options?.skip }
  );
  const timeZoneByBuildingId = useMemo(
    () =>
      new Map(
        (plannerBuildingsData?.buildings ?? []).map((building) => [
          building.buildingId,
          building.gmtZone,
        ])
      ),
    [plannerBuildingsData]
  );

  // Reset the window. Used by filter / sort changes and by refetch().
  const reset = useCallback(() => {
    resettingRef.current = true;
    // If a fetch is already in progress (e.g. from RTK Query tag invalidation),
    // treat it as the post-reset fetch so the processing guard won't block.
    sawFetchAfterResetRef.current = isFetchingRef.current;
    dataAtResetRef.current = dataRef.current;
    setAccumulated([]);
    setAccumulatedSource([]);
    setCursor(undefined);
    setBatchIndex(0);
    setEndReached(false);
    setBatchError(false);
    setTotalCount(undefined);
    inFlightBatchIndexRef.current = null;
    batchStartedAtRef.current = performance.now();
    lastProcessedBatchIndexRef.current = -1;
  }, []);

  useEffect(() => {
    isFetchingRef.current = isFetching;
    dataRef.current = data;
    if (resettingRef.current && (isFetching || data !== dataAtResetRef.current)) {
      sawFetchAfterResetRef.current = true;
    }
  }, [isFetching, data]);

  // Append on successful response (idempotent via lastProcessedBatchIndexRef).
  useEffect(() => {
    if (isFetching || !data || !data.success || !data.data) {
      return;
    }
    // Wait for the buildings query to complete its initial fetch before mapping events.
    // Both queries fire in parallel; if events arrive first, timeZoneByBuildingId is an empty
    // Map and every event would be formatted in local time. Once buildings load, this effect
    // re-fires and the idempotency guard below is not yet set, so events are mapped correctly.
    if (isBuildingsLoading) {
      return;
    }
    if (resettingRef.current && !sawFetchAfterResetRef.current) {
      return;
    }
    // Detect tag-invalidation refetches: data changed for an already-processed batch.
    // Allow reprocessing by clearing the guard so the current batch data is re-mapped.
    if (
      lastProcessedBatchIndexRef.current >= batchIndex &&
      data !== lastProcessedDataRef.current &&
      lastProcessedDataRef.current !== undefined
    ) {
      lastProcessedBatchIndexRef.current = batchIndex - 1;
      // Fall through to reprocess with fresh data.
    }
    if (lastProcessedBatchIndexRef.current >= batchIndex) {
      return;
    }
    lastProcessedBatchIndexRef.current = batchIndex;
    lastProcessedDataRef.current = data;

    resettingRef.current = false;
    sawFetchAfterResetRef.current = false;
    const newRows = mapEventSummariesToPlannerRows(data.data, timeZoneByBuildingId);
    setAccumulatedSource((prevSource) => {
      const seenSourceIds = new Set(prevSource.map((event) => event.eventId));
      const mergedSource =
        batchIndex === 0
          ? data.data
          : prevSource.concat(data.data.filter((event) => !seenSourceIds.has(event.eventId)));

      const numberedTitles = buildSeriesOrdinalTitleMap(
        mergedSource,
        (event) => event.eventTitle ?? 'Untitled Event'
      );

      setAccumulated((prevRows) => {
        if (batchIndex === 0) {
          return newRows.map((row) => ({
            ...row,
            eventName: numberedTitles.get(row.eventId) ?? row.eventName,
          }));
        }

        const seenRowIds = new Set(prevRows.map((row) => row.eventId));
        const mergedRows = prevRows.concat(newRows.filter((row) => !seenRowIds.has(row.eventId)));

        return mergedRows.map((row) => ({
          ...row,
          eventName: numberedTitles.get(row.eventId) ?? row.eventName,
        }));
      });

      return mergedSource;
    });
    const hasMore = data.pagination?.hasMore ?? false;
    const nextToken = data.pagination?.continuationToken;
    // Only trust totalCount from batch 0 of the current filter/sort context.
    // eventServiceApi.transformResponse falls back to `normalized.length` when
    // the backend omits totalCount on paginated batches (page 2+), so the field
    // is always defined but wrong on subsequent batches — it equals the per-page
    // row count. reset() clears totalCount on filter/sort/refetch, so on the
    // next context we re-learn it from the new batch 0.
    if (batchIndex === 0 && data.pagination?.totalCount !== undefined) {
      setTotalCount(data.pagination.totalCount);
    }
    if (!hasMore || !nextToken) {
      setEndReached(true);
    }
    setBatchError(false);
    inFlightBatchIndexRef.current = null;

    const durationMs = Math.max(0, Math.round(performance.now() - batchStartedAtRef.current));
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.API_CALL, {
      apiEndpoint: 'plannerEvents.batchLoad',
      batchIndex: String(batchIndex),
      pageSize: String(pageSize),
      durationMs: String(durationMs),
      status: 'success',
    });
  }, [data, isFetching, batchIndex, pageSize, timeZoneByBuildingId, isBuildingsLoading]);

  // Emit telemetry and surface batchError on error transitions.
  useEffect(() => {
    if (!isError || isFetching) {
      return;
    }
    if (resettingRef.current && !sawFetchAfterResetRef.current) {
      return;
    }
    if (lastProcessedBatchIndexRef.current >= batchIndex) {
      return;
    }
    lastProcessedBatchIndexRef.current = batchIndex;
    resettingRef.current = false;
    sawFetchAfterResetRef.current = false;
    if (batchIndex > 0) {
      setBatchError(true);
    }
    inFlightBatchIndexRef.current = null;
    const durationMs = Math.max(0, Math.round(performance.now() - batchStartedAtRef.current));
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.API_CALL, {
      apiEndpoint: 'plannerEvents.batchLoad',
      batchIndex: String(batchIndex),
      pageSize: String(pageSize),
      durationMs: String(durationMs),
      status: 'failure',
    });
  }, [isError, isFetching, batchIndex, pageSize]);

  const loadMore = useCallback(() => {
    if (inFlightBatchIndexRef.current !== null) return;
    if (endReached) return;
    if (batchError) return;
    const nextToken = data?.pagination?.continuationToken;
    if (!nextToken) return;
    inFlightBatchIndexRef.current = batchIndex + 1;
    batchStartedAtRef.current = performance.now();
    setBatchIndex((i) => i + 1);
    setCursor(nextToken);
  }, [endReached, batchError, data, batchIndex]);

  const retryLastBatch = useCallback(() => {
    if (!batchError) return;
    setBatchError(false);
    // Re-issue the same cursor by lowering the watermark so the success/failure effect re-processes.
    lastProcessedBatchIndexRef.current = batchIndex - 1;
    inFlightBatchIndexRef.current = batchIndex;
    batchStartedAtRef.current = performance.now();
    void refetch();
  }, [batchError, batchIndex, refetch]);

  const isLoadingInitial =
    (isLoading || isFetching || resettingRef.current) && accumulated.length === 0;
  const isLoadingMore = isFetching && accumulated.length > 0 && !batchError;

  const setSortBy = useCallback(
    (next: PlannerSortBy, order: PlannerSortOrder) => {
      reset();
      setSortByState(next);
      setSortOrderState(order);
    },
    [reset]
  );

  const setFilters = useCallback(
    (filters: EventFilters) => {
      const newApiFilters: PlannerApiFilters = {};
      if (filters.statuses.length > 0) {
        newApiFilters.status = mapStatusesToEventStates(filters.statuses);
      }
      if (filters.owners.length > 0) {
        newApiFilters.owner = filters.owners.join(',');
      }
      if (filters.buildings.length > 0) {
        newApiFilters.buildingId = filters.buildings.join(',') as PlannerApiFilters['buildingId'];
      }
      if (filters.eventStartDate) {
        newApiFilters.startDate = toCalendarDate(filters.eventStartDate);
      }
      if (filters.eventEndDate) {
        newApiFilters.endDate = toCalendarDate(filters.eventEndDate);
      }
      if (filters.submissionStartDate) {
        newApiFilters.submittedStartDate = toCalendarDate(filters.submissionStartDate);
      }
      if (filters.submissionEndDate) {
        newApiFilters.submittedEndDate = toCalendarDate(filters.submissionEndDate);
      }
      reset();
      setApiFilters(newApiFilters);
    },
    [reset]
  );

  const clearFilters = useCallback(() => {
    reset();
    setApiFilters({});
  }, [reset]);

  // Push the search term to the backend. Resets the accumulating window so the next
  // batch 0 reflects the exhaustive server-side search rather than appending to the
  // previously loaded (unsearched) page. An empty/whitespace term clears the search.
  const setSearch = useCallback(
    (next: string) => {
      const trimmed = next.trim();
      reset();
      setSearchState(trimmed.length > 0 ? trimmed : undefined);
    },
    [reset]
  );

  const applyRowMutation = useCallback((eventId: string, patch: Partial<PlannerEventRow>) => {
    setAccumulated((prev) =>
      prev.map((row) => (row.eventId === eventId ? { ...row, ...patch } : row))
    );
  }, []);

  const userRefetch = useCallback(() => {
    reset();
    void refetch();
  }, [reset, refetch]);

  return {
    events: accumulated,
    isLoadingInitial,
    isLoadingMore,
    isError: isError && accumulated.length === 0,
    batchError,
    endReached,
    totalCount,
    sortBy,
    sortOrder,
    pageSize,
    loadMore,
    retryLastBatch,
    refetch: userRefetch,
    setSortBy,
    setFilters,
    setSearch,
    clearFilters,
    applyRowMutation,
  };
}
